import { type ApiTemplateCustomization } from '@Model';
import {
    ArrowsControl,
    ArrowsControlValue,
    Badge,
    FlexRow,
    NumberInput,
    parseAsFloat,
} from '@synqo/frontend-component-library';
import { type FC, useCallback, useMemo } from 'react';
import { type GroupControlProps } from '../props';
import { useUpdateCustomization } from '../useUpdateCustomization';

interface AxisInputProps {
    axis: 'X' | 'Y';
    customization?: ApiTemplateCustomization;
    value: number;
    onChange: (newValue: number) => boolean;
    locked?: boolean;
}

const SingleOffsetControl: FC<AxisInputProps> = ({ axis, customization, value, onChange, locked }) => {
    if (!customization) return null;

    return (
        <FlexRow width="150px" gap="xs" label={customization.label} align="center" justify="center">
            <Badge variant="primary" type="input" size="sm" width="fitContent">
                {axis}
            </Badge>
            <NumberInput
                name={customization.name}
                type="number"
                value={value}
                onChange={onChange}
                locked={locked}
                readOnly={locked}
                ariaLabel="Wert in mm"
                valid
            />
            mm
        </FlexRow>
    );
};

export const OffsetControl: FC<GroupControlProps> = ({ data, locked, updateData }) => {
    const customizations = data.customizations;

    const [offsetCustomizationX, offsetCustomizationY] = useMemo(
        () => [
            customizations.find((c) => c.keyword === 'x_offset'),
            customizations.find((c) => c.keyword === 'y_offset'),
        ],
        [customizations],
    );

    const { state: stateX, setState: setStateX } = useUpdateCustomization(offsetCustomizationX, updateData);
    const { state: stateY, setState: setStateY } = useUpdateCustomization(offsetCustomizationY, updateData);

    const { numberStateX, numberStateY, combinedState } = useMemo(() => {
        const numberStateX = parseAsFloat(stateX);
        const numberStateY = parseAsFloat(stateY);
        const combinedState: ArrowsControlValue = { x: numberStateX, y: numberStateY };
        return {
            numberStateX,
            numberStateY,
            combinedState,
        };
    }, [stateX, stateY]);

    const onSetX = useCallback((x: number) => setStateX(x.toString()), [setStateX]);
    const onSetY = useCallback((y: number) => setStateY(y.toString()), [setStateY]);
    const setCombinedState = useCallback(
        (c: ArrowsControlValue) => {
            onSetX(c.x);
            onSetY(c.y);
        },
        [onSetX, onSetY],
    );

    return (
        <FlexRow>
            <SingleOffsetControl
                axis="X"
                customization={offsetCustomizationX}
                value={numberStateX}
                onChange={onSetX}
                locked={locked}
            />
            <SingleOffsetControl
                axis="Y"
                customization={offsetCustomizationY}
                value={numberStateY}
                onChange={onSetY}
                locked={locked}
            />
            <ArrowsControl
                value={combinedState}
                onChange={setCombinedState}
                locked={locked}
                hideX={!offsetCustomizationX}
                hideY={!offsetCustomizationY}
            />
        </FlexRow>
    );
};
